@if (title() || closable() || historyEnabled()) {
  <div class="chat-toolbar">
    @if (title()) {
      <h2 class="chat-toolbar__title">{{ title() }}</h2>
    }
    <div class="chat-toolbar__actions">
      @if (historyEnabled()) {
        <button
          mat-stroked-button
          type="button"
          class="chat-toolbar__button"
          [attr.aria-label]="'AI_ASSISTANT.SETTINGS.GENERATION_CHAT.HISTORY.OPEN_HISTORY' | translate"
          (click)="openHistoryPanel()"
        >
          {{'AI_ASSISTANT.SETTINGS.GENERATION_CHAT.HISTORY.TITLE' | translate}}
        </button>
        <button
          mat-icon-button
          type="button"
          class="chat-toolbar__button"
          [attr.aria-label]="'AI_ASSISTANT.SETTINGS.GENERATION_CHAT.HISTORY.NEW_CHAT' | translate"
          (click)="onHistoryNewChatRequested()"
        >
          <mat-icon>add</mat-icon>
        </button>
      }
      @if (closable()) {
        <button
          mat-icon-button
          type="button"
          class="chat-toolbar__button"
          aria-label="Close"
          (click)="onClose()"
        >
          <mat-icon>close</mat-icon>
        </button>
      }
    </div>
  </div>
}

<glxy-chat-container #chatContainer [chatId]="chatId()">
  @if (chatService.error()) {
    <glxy-chat-event>Error: {{ chatService.error() }}</glxy-chat-event>
  }

  @if (chatService.isStreaming()) {
    <glxy-chat-message-group
      type="received"
      [messageFrom]="senderName()"
      [profileIcon]="senderIcon()"
      [isTyping]="true"
    >
    </glxy-chat-message-group>
  }

  @for (message of chatService.messages().slice().reverse(); track message.id) {
    @switch (message.type) {
      @case ('user') {
        <glxy-chat-message-group type="sent" [sentFromYou]="true" messageFrom="You">
          <glxy-chat-message [messageText]="message.text"></glxy-chat-message>
        </glxy-chat-message-group>
      }
      @case ('assistant-text') {
        <glxy-chat-message-group
          type="received"
          [messageFrom]="senderName()"
          [profileIcon]="senderIcon()"
        >
          <glxy-chat-message [messageText]="preserveNewlines(message.text)" [messageIsMarkdown]="true"></glxy-chat-message>
        </glxy-chat-message-group>
      }
      @case ('working') {
        <glxy-chat-message-group type="received" [messageFrom]="senderName()" [profileIcon]="senderIcon()">
          <glxy-chat-message [noBubble]="true">
            @let allDone = allResolved(message);
            <details class="working-group" [open]="!allDone">
              <summary class="working-summary">
                @if (allDone) {
                  <mat-icon class="working-icon working-icon--done">check_circle</mat-icon>
                  <span>Finished {{ message.toolCalls.length }} action{{ message.toolCalls.length === 1 ? '' : 's' }}</span>
                } @else {
                  <mat-icon class="working-icon working-icon--running">autorenew</mat-icon>
                  <span>Working&hellip;</span>
                }
                <mat-icon class="working-chevron">expand_more</mat-icon>
              </summary>
              <ul class="working-list">
                @for (tc of message.toolCalls; track tc.functionCallId) {
                  <li
                    class="working-item"
                    [class.working-item--running]="tc.state === 'running'"
                    [class.working-item--error]="tc.state === 'error'"
                  >
                    <mat-icon class="working-item-icon">
                      {{ tc.state === 'complete' ? 'check' : tc.state === 'error' ? 'error' : 'sync' }}
                    </mat-icon>
                    <span class="working-item-name">{{ tc.functionName }}</span>
                  </li>
                }
              </ul>
            </details>
          </glxy-chat-message>
        </glxy-chat-message-group>
      }
      @case ('plan') {
        <glxy-chat-message-group type="received" [messageFrom]="senderName()" [profileIcon]="senderIcon()">
          <glxy-chat-message [noBubble]="true">
            <ai-assistant-plan-display
              [plan]="message.plan"
              [action]="message.action"
              (approved)="onPlanApproved($event)"
              (rejected)="onPlanRejected()"
            ></ai-assistant-plan-display>
          </glxy-chat-message>
        </glxy-chat-message-group>
      }
      @case ('questions') {
        <glxy-chat-message-group type="received" [messageFrom]="senderName()" [profileIcon]="senderIcon()">
          <glxy-chat-message [noBubble]="true">
            <ai-assistant-questions-display
              [questions]="message.questions"
              [outputText]="message.output"
              (answered)="onQuestionsAnswered(message.questions, $event)"
              (cancelled)="onQuestionsCancelled()"
            ></ai-assistant-questions-display>
          </glxy-chat-message>
        </glxy-chat-message-group>
      }
      @case ('proposed-action') {
        <glxy-chat-message-group type="received" [messageFrom]="senderName()" [profileIcon]="senderIcon()">
          <glxy-chat-message [noBubble]="true">
            <ai-proposed-action
              #proposedActionRef
              [namespaceId]="message.proposedAction.namespaceId"
              [namespaceType]="message.proposedAction.namespaceType"
              [sessionId]="chatService.sessionId"
              [eventId]="message.proposedAction.sessionEventId"
              (approved)="onProposedActionApproved($event, proposedActionRef)"
              (rejected)="onProposedActionRejected($event, proposedActionRef)"
              (changeRequested)="onProposedActionChangeRequested()"
            />
          </glxy-chat-message>
        </glxy-chat-message-group>
      }
    }
  }
</glxy-chat-container>

@if (chatService.messages().length === 0) {
  @if (!autoLoadResolved()) {
    <div class="hints-loading">
      <glxy-loading-spinner></glxy-loading-spinner>
    </div>
  } @else if (hints().length) {
    <div class="hints">
      <p class="hints__label">{{ 'AI_ASSISTANT.SETTINGS.GENERATION_CHAT.TRY_SAYING' | translate }}</p>
      @for (hint of hints(); track hint) {
        <button
          class="hint"
          (click)="onHintClicked(hint)"
        >
          {{ hint }}
        </button>
      }
    </div>
  }
}

<glxy-chat-composer
  #composer
  [placeholder]="placeholder()"
  sendText="{{ 'GALAXY.CHAT_COMPOSER.SEND' | translate }}"
  [textAreaMinRows]="2"
  [textAreaMaxRows]="5"
  [processing]="chatService.isStreaming()"
  [enterKeySendsMessage]="true"
  (send)="onSend($event)"
></glxy-chat-composer>

@if (historyPanelOpen()) {
  <div class="history-backdrop" (click)="closeHistoryPanel()"></div>
  <ai-generation-chat-history
    class="history-panel"
    [namespace]="chatService.namespace() ?? null"
    [tags]="chatService.sessionTags()"
    [activeSessionId]="chatService.sessionId"
    (sessionSelected)="onHistorySessionSelected($event)"
    (newChatRequested)="onHistoryNewChatRequested()"
    (closed)="closeHistoryPanel()"
  ></ai-generation-chat-history>
}
